'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { X, DollarSign, Loader2 } from 'lucide-react';

import type { Solution } from './mockData';
import PayFastCheckout from '@/components/PayFastCheckout';
import FlutterwaveCheckout from '@/components/FlutterwaveCheckout';
import PayNowCheckout from '@/components/PayNowCheckout';
import { useAuth } from '@/contexts/AuthContext';

interface FundingOfferFormData {
  solutionId: string;
  amount: string;
  equity: string;
  instrumentType: string;
  valuationCap: string;
  discount: string;
  termMonths: string;
  notes: string;
}

interface FundingOfferFormProps {
  problemTitle: string;
  solutions: Solution[];
  onClose: () => void;
}

const INSTRUMENT_TYPES = ['SAFE Note', 'Convertible Note', 'Equity Round', 'Grant (No Equity)', 'Revenue Share'];

export default function FundingOfferForm({ problemTitle, solutions, onClose }: FundingOfferFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [confirmedData, setConfirmedData] = useState<FundingOfferFormData | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'payfast' | 'flutterwave' | 'paynow'>('payfast');
  const { user } = useAuth();
  const router = useRouter();

  const { register, handleSubmit, formState: { errors } } = useForm<FundingOfferFormData>({
    defaultValues: {
      solutionId: solutions[0]?.id ?? '',
      instrumentType: 'SAFE Note',
    },
  });

  async function onSubmit(data: FundingOfferFormData) {
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    setConfirmedData(data);
    setShowPayment(true);
  }

  const paymentId = confirmedData
    ? `offer-${confirmedData.solutionId}-${Date.now()}`
    : '';

  const userMeta = user?.user_metadata || {};
  const fullName: string = userMeta.full_name || '';
  const nameParts = fullName.trim().split(' ');
  const nameFirst = nameParts[0] || '';
  const nameLast = nameParts.slice(1).join(' ') || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/30 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-2xl card-shadow-lg border border-border w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
          <div>
            <h3 className="text-base font-bold text-foreground">
              {showPayment ? 'Confirm & Pay' : 'Submit Funding Offer'}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              For: {problemTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close funding offer form"
          >
            <X size={16} className="text-muted-foreground" />
          </button>
        </div>

        {showPayment && confirmedData ? (
          /* Payment confirmation step */
          <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-5">
            <div className="bg-muted/50 border border-border rounded-xl p-4 space-y-2">
              <h4 className="text-sm font-semibold text-foreground mb-3">Investment Summary</h4>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Amount (ZAR)</span>
                <span className="font-bold text-foreground">R {parseFloat(confirmedData.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Equity</span>
                <span className="font-semibold text-foreground">{confirmedData.equity}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Instrument</span>
                <span className="font-semibold text-foreground">{confirmedData.instrumentType}</span>
              </div>
              {confirmedData.termMonths && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Term</span>
                  <span className="font-semibold text-foreground">{confirmedData.termMonths} months</span>
                </div>
              )}
            </div>

            {/* Payment Method Selector */}
            <div>
              <p className="text-sm font-semibold text-foreground mb-2">Choose Payment Gateway</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('payfast')}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition-all ${
                    paymentMethod === 'payfast' ?'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/50 hover:bg-muted'
                  }`}
                >
                  <span className="text-base">🇿🇦</span>
                  <span>PayFast</span>
                  <span className="text-[10px] font-normal opacity-70">South Africa</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('flutterwave')}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition-all ${
                    paymentMethod === 'flutterwave' ?'border-orange-500 bg-orange-500/10 text-orange-600' :'border-border text-muted-foreground hover:border-orange-400/50 hover:bg-muted'
                  }`}
                >
                  <span className="text-base">🌍</span>
                  <span>Flutterwave</span>
                  <span className="text-[10px] font-normal opacity-70">SADC Region</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod('paynow')}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-semibold transition-all ${
                    paymentMethod === 'paynow' ?'border-red-600 bg-red-600/10 text-red-600' :'border-border text-muted-foreground hover:border-red-500/50 hover:bg-muted'
                  }`}
                >
                  <span className="text-base">🇿🇼</span>
                  <span>PayNow</span>
                  <span className="text-[10px] font-normal opacity-70">Zimbabwe</span>
                </button>
              </div>
            </div>

            <div className="bg-warning-bg border border-warning/20 rounded-xl p-3">
              <p className="text-xs text-warning font-semibold mb-0.5">Important</p>
              <p className="text-xs text-foreground/70 leading-relaxed">
                {paymentMethod === 'payfast' ?'You will be redirected to PayFast to complete your payment securely. Best for South African investors. All amounts are in ZAR.'
                  : paymentMethod === 'flutterwave' ?'You will be redirected to Flutterwave to complete your payment. Supports SADC countries including ZA, ZW, ZM, TZ, MZ and more.' :'You will be redirected to PayNow to complete your payment. Supports EcoCash, OneMoney, and ZIPIT for Zimbabwean investors.'}
              </p>
            </div>

            {paymentMethod === 'payfast' ? (
              <PayFastCheckout
                amount={parseFloat(confirmedData.amount)}
                itemName={`Monezite Investment - ${problemTitle}`.slice(0, 100)}
                itemDescription={`${confirmedData.instrumentType} · ${confirmedData.equity}% equity`.slice(0, 255)}
                paymentId={paymentId}
                nameFirst={nameFirst}
                nameLast={nameLast}
                email={user?.email || ''}
                customStr1={user?.id || ''}
                onCancel={() => setShowPayment(false)}
                buttonLabel={`Pay R ${parseFloat(confirmedData.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })} via PayFast`}
              />
            ) : paymentMethod === 'flutterwave' ? (
              <FlutterwaveCheckout
                amount={parseFloat(confirmedData.amount)}
                itemName={`Monezite Investment - ${problemTitle}`.slice(0, 100)}
                itemDescription={`${confirmedData.instrumentType} · ${confirmedData.equity}% equity`.slice(0, 255)}
                paymentId={paymentId}
                nameFirst={nameFirst}
                nameLast={nameLast}
                email={user?.email || ''}
                customStr1={user?.id || ''}
                onCancel={() => setShowPayment(false)}
                buttonLabel={`Pay R ${parseFloat(confirmedData.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })} via Flutterwave`}
              />
            ) : (
              <PayNowCheckout
                amount={parseFloat(confirmedData.amount)}
                itemName={`Monezite Investment - ${problemTitle}`.slice(0, 100)}
                itemDescription={`${confirmedData.instrumentType} · ${confirmedData.equity}% equity`.slice(0, 255)}
                paymentId={paymentId}
                email={user?.email || ''}
                onCancel={() => setShowPayment(false)}
                buttonLabel={`Pay via PayNow Zimbabwe`}
              />
            )}
          </div>
        ) : (
          /* Form step */
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
            {/* Solution Selector */}
            {solutions.length > 1 && (
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="solutionId">
                  Target Solution
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Select which solution proposal you are funding</p>
                <select
                  id="solutionId"
                  className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                  {...register('solutionId', { required: 'Select a solution to fund' })}
                >
                  {solutions.map((s) => (
                    <option key={`sol-opt-${s.id}`} value={s.id}>
                      {s.title} — by {s.entrepreneur}
                    </option>
                  ))}
                </select>
                {errors.solutionId && <p className="mt-1 text-xs text-danger font-medium">{errors.solutionId.message}</p>}
              </div>
            )}

            {/* Investment Amount */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="offer-amount">
                Investment Amount (ZAR)
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">The total capital you are committing to this deal</p>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-semibold">R</span>
                <input
                  id="offer-amount"
                  type="number"
                  placeholder="e.g. 75000"
                  className={`w-full pl-9 pr-4 py-3 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors tabular-nums ${errors.amount ? 'border-danger' : 'border-input'}`}
                  {...register('amount', {
                    required: 'Investment amount is required',
                    min: { value: 5, message: 'Minimum investment is R5.00' },
                    max: { value: 10000000, message: 'Maximum single offer is R10M' },
                  })}
                />
              </div>
              {errors.amount && <p className="mt-1 text-xs text-danger font-medium">{errors.amount.message}</p>}
            </div>

            {/* Instrument Type */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="instrumentType">
                Investment Instrument
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">The legal structure of your investment</p>
              <select
                id="instrumentType"
                className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
                {...register('instrumentType', { required: true })}
              >
                {INSTRUMENT_TYPES.map((t) => (
                  <option key={`instrument-${t}`} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Equity + Valuation Cap in 2 cols */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="equity">
                  Equity % Ask
                </label>
                <input
                  id="equity"
                  type="number"
                  step="0.5"
                  placeholder="e.g. 10"
                  className={`w-full px-4 py-3 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors tabular-nums ${errors.equity ? 'border-danger' : 'border-input'}`}
                  {...register('equity', {
                    required: 'Equity % is required',
                    min: { value: 0, message: 'Cannot be negative' },
                    max: { value: 100, message: 'Cannot exceed 100%' },
                  })}
                />
                {errors.equity && <p className="mt-1 text-xs text-danger font-medium">{errors.equity.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="valuationCap">
                  Valuation Cap (R)
                </label>
                <input
                  id="valuationCap"
                  type="number"
                  placeholder="e.g. 1500000"
                  className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors tabular-nums"
                  {...register('valuationCap')}
                />
              </div>
            </div>

            {/* Discount + Term in 2 cols */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="discount">
                  Discount % (if note)
                </label>
                <input
                  id="discount"
                  type="number"
                  step="1"
                  placeholder="e.g. 20"
                  className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors tabular-nums"
                  {...register('discount')}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="termMonths">
                  Term (months)
                </label>
                <input
                  id="termMonths"
                  type="number"
                  placeholder="e.g. 24"
                  className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors tabular-nums"
                  {...register('termMonths')}
                />
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="offer-notes">
                Additional Terms & Notes
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Board seat requirements, milestone conditions, co-investor preferences, or any other deal-specific terms
              </p>
              <textarea
                id="offer-notes"
                rows={4}
                placeholder="e.g. We require a board observer seat and quarterly impact reporting. Open to co-investing with another Monezite investor..."
                className="w-full px-4 py-3 bg-card border border-input rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors resize-none"
                {...register('notes')}
              />
            </div>

            {/* Disclaimer */}
            <div className="bg-warning-bg border border-warning/20 rounded-xl p-3">
              <p className="text-xs text-warning font-semibold mb-0.5">Important</p>
              <p className="text-xs text-foreground/70 leading-relaxed">
                Clicking "Continue to Payment" will let you choose between PayFast (South Africa), Flutterwave (SADC region), or PayNow (Zimbabwe) to complete your investment securely.
              </p>
            </div>
          </form>
        )}

        {/* Footer — only shown on form step */}
        {!showPayment && (
          <div className="flex gap-3 p-5 border-t border-border shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-border rounded-xl text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors active:scale-95"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ minHeight: '48px' }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <DollarSign size={15} />
                  Continue to Payment
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}